import { useEffect, useState } from "react";
import { Wallet, Transaction } from "../types";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import { fetchCountryConfig } from "../API/services";
import { useCurrencyStore } from "../../../stores/useCurrencyStore";
import { cache } from "../../../utils";
import Spinner from "../../ui/Spinner/Spinner";
import BankTransferWithdrawModal from "./BankTransferWithdrawalModal";
import CreatePinModal from "../../auth/CreatePin";
import MobileMoneyWithdrawModal from "./MobileMoneyWithdrawModal";
import { AlertCircle, ChevronDownIcon } from "lucide-react";
import { fetchBanks, fetchMobileNetworks } from "../../Transactions/Apis/PaymentMethods";
import { CONFIG_TYPE, WALLET_MOBILE_MONEY_CURRENCIES } from "../../../utils/constant";
import { fetchTransactionFee } from "../../Transactions/Apis/exchangeRates";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: Wallet | null;
  onSuccess: (transaction?: Transaction) => void;
}

const WithdrawModal = ({ isOpen, onClose, wallet, onSuccess }: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [showCreatePinModal, setShowCreatePinModal] = useState(false);
  const [countryConfig, setCountryConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [showMobileMoneyModal, setShowMobileMoneyModal] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [displayAmount, setDisplayAmount] = useState<string>("");
  const [calculatedFee, setCalculatedFee] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isAmountValid, setIsAmountValid] = useState(false);
  const [banks, setBanks] = useState<any[]>([]);
  const [networks, setNetworks] = useState<any[]>([]);
  const [pinRequired, setPinRequired] = useState(false);
  
  // New states for supporting countries
  const [supportingCountries, setSupportingCountries] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [selectedCurrencyData, setSelectedCurrencyData] = useState<any>(null);

  const { walletSupportedCurrencies } = useCurrencyStore();
  const baseCurrencyData = walletSupportedCurrencies.find(c => c.code === wallet?.currency);
  const hasSupportingCountries = baseCurrencyData?.hasSupportingCountries;
  
  // ---- Load supporting countries when modal opens
  useEffect(() => {
    if (isOpen && wallet?.currency && baseCurrencyData) {
      resetForm();
      
      if (hasSupportingCountries && baseCurrencyData?.supportingCountries) {
        // Convert supportingCountries object to array and filter by allowWalletCreation
        const countriesArray = Object.values(baseCurrencyData.supportingCountries)
          .filter((country: any) => country.allowWalletCreation === true);
        
        setSupportingCountries(countriesArray);
        
        // If there's only one country, preselect it (like XAF)
        if (countriesArray.length === 1) {
          setSelectedCountry(countriesArray[0]);
          setSelectedCurrencyData(countriesArray[0]);
        } else if (countriesArray.length > 1) {
          // For XOF with multiple countries, don't auto-select
          // Just set the base currency data initially
          setSelectedCurrencyData(baseCurrencyData);
        }
      } else {
        // For currencies without supporting countries, use the base currency data
        setSelectedCurrencyData(baseCurrencyData);
      }
    }
  }, [isOpen, wallet?.currency, baseCurrencyData, hasSupportingCountries]);

  // ---- Fetch config and providers when selected country changes
  useEffect(() => {
    const loadConfig = async () => {
      // For currencies with supporting countries, only fetch config when a country is selected
      if (hasSupportingCountries && !selectedCountry) {
        // Don't fetch config yet for currencies with multiple countries
        setCountryConfig(null);
        setBanks([]);
        setNetworks([]);
        return;
      }
      
      // Determine which data to use for fetching config
      const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;
      
      if (!dataToUse?.iso2 || !isOpen) return;
      
      setLoadingConfig(true);
      setError(null);
      setValidationErrors([]);
      setBanks([]);
      setNetworks([]);
      
      try {
        // Fetch country config using selected country's iso2
        const config = await fetchCountryConfig(dataToUse.iso2);
        if (config) {
          setCountryConfig(config);
        } else {
          setError("Failed to load withdrawal configuration");
        }

        // Check if currency supports bank transfer or mobile money
        const supportsBankTransfer = wallet?.currency === 'NGN';
        const supportsMobileMoney = WALLET_MOBILE_MONEY_CURRENCIES.includes(wallet?.currency!);

        // Fetch banks for bank transfer
        if (supportsBankTransfer) {
          setLoadingBanks(true);
          try {
            const banksData = await fetchBanks(dataToUse.code || wallet?.currency);
            setBanks(banksData);
          } catch (err) {
            console.error("Failed to load banks:", err);
            setBanks([]);
          } finally {
            setLoadingBanks(false);
          }
        }

        // Fetch mobile networks for mobile money
        if (supportsMobileMoney) {
          setLoadingNetworks(true);
          try {
            const networksData = await fetchMobileNetworks(
              dataToUse.code || wallet?.currency,
              dataToUse.iso2
            );
            const filteredNetworks = networksData.filter((networksData: any) => {
              if (dataToUse.code === "KES" && networksData?.name === "AIRTEL") {
                return false;
              }
              return true;
            });
            setNetworks(filteredNetworks);
          } catch (err) {
            console.error("Failed to load mobile networks:", err);
            setNetworks([]);
          } finally {
            setLoadingNetworks(false);
          }
        }
      } catch (err) {
        setError("Failed to load configuration");
        console.error("Error loading data:", err);
      } finally {
        setLoadingConfig(false);
      }
    };

    if (isOpen) {
      loadConfig();
    }
  }, [
    selectedCountry?.iso2, 
    selectedCurrencyData?.iso2, 
    isOpen, 
    wallet?.currency, 
    hasSupportingCountries
  ]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setDisplayAmount("");
      setError(null);
      setValidationErrors([]);
      setCalculatedFee(0);
      setIsAmountValid(false);
      setShowBankTransferModal(false);
      setShowMobileMoneyModal(false);
      setShowCreatePinModal(false);
      setBanks([]);
      setNetworks([]);
      setSupportingCountries([]);
      setSelectedCountry(null);
      setShowCountryDropdown(false);
      setSelectedCurrencyData(null);
      setCountryConfig(null);
      setLoadingBanks(false);
      setLoadingNetworks(false);
    }
  }, [isOpen]);

  // ---- Calculate fee when amount or config changes
  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (amount && countryConfig) {
        const fee = await calculateFee();
        if (isMounted) {
          setCalculatedFee(fee);
          validateAmount();
        }
      } else {
        if (isMounted) {
          setCalculatedFee(0);
          setIsAmountValid(false);
          setValidationErrors([]);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [amount, countryConfig, wallet?.balance]);

  // ---- Check if user has a PIN set
  const checkUserPin = () => {
    const user = cache.get("USER") as { hasPin: boolean } | null;
    if (!user?.hasPin) {
      setShowCreatePinModal(true);
      setPinRequired(true);
      return false;
    }

    return true;
  };
    
const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;

  // ---- Calculate fee based on country config and amount
  const calculateFee = async (): Promise<number> => {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return 0;
    }

    const walletIso2 = wallet?.currency?.slice(0, -1);
    
    let fee = await fetchTransactionFee(amountNum, dataToUse?.iso2 || walletIso2 || '', CONFIG_TYPE.WITHDRAWAL_FEE);
    
    return fee;

  }; 
  const getFeeAndTotal = () => {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      return { fee: 0, total: 0, amountToReceive: 0 };
    }
    
    const fee = calculatedFee;
    const total = amountNum + fee;
    const amountToReceive = amountNum;
    
    return { fee, total, amountToReceive };
  };

  const validateAmount = () => {
    const amountNum = parseFloat(amount);
    const errors: string[] = [];
    let isValid = true;

    // Check if amount is empty or invalid
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      setIsAmountValid(false);
      setValidationErrors([]);
      return false;
    }

    // Check minimum transfer
    if (countryConfig?.minTransfer) {
      const minTransfer = parseFloat(countryConfig.minPayout?.value || countryConfig.minTransfer.value);
      if (amountNum < minTransfer) {
        isValid = false;
      }
    }

    // Check maximum transfer
    if (countryConfig?.maxTransfer) {
      const maxTransfer = parseFloat(countryConfig.maxPayout?.value || countryConfig.maxTransfer.value);
      if (amountNum > maxTransfer) {
        isValid = false;
      }
    }

    // Check min/max range
    if (countryConfig?.minTransfer && countryConfig?.maxTransfer) {
      const minTransfer = parseFloat(countryConfig.minPayout?.value || countryConfig.minTransfer.value);
      const maxTransfer = parseFloat(countryConfig.maxPayout?.value || countryConfig.maxTransfer.value);
      if (amountNum < minTransfer || amountNum > maxTransfer) {
        if (!errors.length) {
          errors.push(`Amount must be between ${wallet?.symbol}${minTransfer.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} and ${wallet?.symbol}${maxTransfer.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`);
        }
      }
    }

    const { total } = getFeeAndTotal();
    if (total > (wallet?.balance || 0)) {
      errors.push("Insufficient balance");
      isValid = false;
    }

    setValidationErrors(errors);
    setIsAmountValid(isValid && errors.length === 0);
    
    return isValid;
  };

  const handleContinue = () => {
    if (!validateAmount()) return;
    
    // For currencies with supporting countries, require country selection
    if (hasSupportingCountries && !selectedCountry) {
      setError("Please select a country");
      return;
    }

    const dataToUse = hasSupportingCountries && selectedCountry ? selectedCountry : selectedCurrencyData;

    // Check PIN before proceeding
    const canProceed = checkUserPin();
    if (!canProceed) return;

    const supportsBankTransfer = wallet?.currency === 'NGN';
    const supportsMobileMoney = WALLET_MOBILE_MONEY_CURRENCIES.includes(wallet?.currency!);

    if (supportsBankTransfer) {
      setShowBankTransferModal(true);
    } else if (supportsMobileMoney) {
      setShowMobileMoneyModal(true);
    } else {
      setError(`Withdrawal not supported for ${dataToUse?.name || wallet?.currency}`);
    }
  };

  const renderFeeAndAmounts = () => {
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0 || !countryConfig) return null;

    const { fee, amountToReceive, total } = getFeeAndTotal();

    return (
      <div className="mb-6 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Fee:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet?.symbol} {fee.toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Amount to Receive:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet?.symbol} {amountToReceive.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-3 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {wallet?.symbol} {total.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
      </div>
    );
  };

  const formatNumberWithCommas = (value: string): string => {
    if (!value) return "";
    const cleanValue = value.replace(/[^\d.]/g, "");
    const parts = cleanValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    const cleanValue = inputValue.replace(/[^\d.,]/g, "");
    
    const decimalParts = cleanValue.split(".");
    let processedValue = cleanValue;
    if (decimalParts.length > 2) {
      processedValue = decimalParts[0] + "." + decimalParts.slice(1).join("");
    }
    
    const rawValue = processedValue.replace(/,/g, "");
    
    if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
      setAmount(rawValue);
      setDisplayAmount(formatNumberWithCommas(rawValue));
      setError("");
    }
  };

  const resetForm = () => {
    setAmount("");
    setDisplayAmount("");
    setError(null);
    setValidationErrors([]);
    setCalculatedFee(0);
    setIsAmountValid(false);
    setShowBankTransferModal(false);
    setShowMobileMoneyModal(false);
    setShowCreatePinModal(false);
  };

  const handlePinCreated = () => {
    setShowCreatePinModal(false);
    setPinRequired(false);

    const user = cache.get("USER");
    if (user) {
      cache.set("USER", { ...user, hasPin: true });
    }
  };

  if (!wallet || !baseCurrencyData) return null;

  // Determine which data to use
  const supportsBankTransfer = wallet.currency === 'NGN';
  const supportsMobileMoney = WALLET_MOBILE_MONEY_CURRENCIES.includes(wallet.currency);

  // Check if still loading banks/networks for the selected withdrawal method
  const isLoadingRequiredData = 
    (supportsBankTransfer && loadingBanks) || 
    (supportsMobileMoney && loadingNetworks);

  return (
    <>
      <Modal
        isOpen={isOpen && !showBankTransferModal && !showMobileMoneyModal && !showCreatePinModal}
        onClose={onClose}
        className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900"
      >
        <h2 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
          Withdraw from {wallet.name} Wallet
        </h2>
        
        <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
          <div className="text-gray-500 dark:text-gray-400">
            Available balance: {wallet.symbol} {wallet.balance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
        </p>

        {error && (
          <div className="mb-4">
            <Alert 
              variant="error" 
              title="Error" 
              message={error}
            />
          </div>
        )}

        {/* Country selection for currencies with supporting countries */}
        {hasSupportingCountries && supportingCountries.length > 0 && (
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Country
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="flex w-full items-center rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-800 hover:bg-gray-100 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
              >
                {selectedCountry ? (
                  <>
                    <img
                      src={selectedCountry.flag}
                      alt={selectedCountry.country}
                      className="mr-2 h-5 w-5 rounded-full"
                    />
                    <span>{selectedCountry.country}</span>
                    <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
                      (+{selectedCountry.dialCode})
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">
                    Select a country
                  </span>
                )}
                <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
              </button>

              {showCountryDropdown && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {supportingCountries.map((country) => (
                    <div
                      key={`${country.code}-${country.iso2}`}
                      onClick={() => {
                        setSelectedCountry(country);
                        setSelectedCurrencyData(country);
                        setShowCountryDropdown(false);
                        // Don't reset amount when re-selecting same country
                        if (selectedCountry?.iso2 !== country.iso2) {
                          setAmount("");
                          setDisplayAmount("");
                        }
                      }}
                      className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <img
                        src={country.flag}
                        alt={country.country}
                        className="mr-2 h-5 w-5 rounded-full"
                      />
                      <div className="flex flex-col">
                        <span className="text-gray-700 dark:text-white">
                          {country.country}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          +{country.dialCode}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading states */}
        {(loadingConfig || isLoadingRequiredData) && (
          <div className="mb-6 flex justify-center">
            <div className="flex flex-col items-center gap-2">
              <Spinner className="h-4 w-4" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {loadingConfig && "Loading..."}
                {!loadingConfig && loadingBanks && "Loading banks..."}
                {!loadingConfig && loadingNetworks && "Loading mobile networks..."}
              </span>
            </div>
          </div>
        )}

        {/* Show amount input only if no supporting countries OR selected country exists */}
        {(!hasSupportingCountries || selectedCountry) && !loadingConfig && !isLoadingRequiredData && (
          <>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Amount ({wallet.symbol})
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={displayAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  disabled={loadingConfig || (hasSupportingCountries && !selectedCountry) || isLoadingRequiredData}
                  className={`w-full rounded-lg border bg-gray-50 pl-12 pr-4 py-3 text-lg text-gray-900 transition-colors focus:outline-none focus:ring-2 disabled:opacity-50 dark:bg-gray-800 dark:text-white ${
                    validationErrors.length > 0 
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/30 dark:border-red-700" 
                      : "border-gray-200 focus:border-primary/50 focus:ring-primary/30 dark:border-gray-700"
                  }`}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
                  {wallet.symbol}
                </span>
              </div>
              
              {/* Validation error messages */}
              {validationErrors.length > 0 && (
                <div className="mt-2 space-y-1">
                  {validationErrors.map((error, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                    >
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fee and Amounts Breakdown */}
            {countryConfig && renderFeeAndAmounts()}
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={onClose}
                disabled={loadingConfig || isLoadingRequiredData}
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              
              <button
                onClick={handleContinue}
                disabled={
                  loadingConfig ||
                  isLoadingRequiredData ||
                  !amount ||
                  parseFloat(amount) <= 0 ||
                  !isAmountValid ||
                  validationErrors.length > 0 ||
                  (hasSupportingCountries && !selectedCountry) ||
                  (!supportsBankTransfer && !supportsMobileMoney)
                }
                className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Continue
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* Create PIN Modal */}
      <CreatePinModal
        isOpen={showCreatePinModal}
        onClose={() => {
          setShowCreatePinModal(false);
          if (pinRequired) {
            onClose();
          }
        }}
        onSuccess={handlePinCreated}
      />

      {/* Bank Transfer Modal */}
      {supportsBankTransfer && wallet && dataToUse && (
        <BankTransferWithdrawModal
          isOpen={showBankTransferModal}
          onClose={() => {
            setShowBankTransferModal(false);
            onClose();
          }}
          onSuccess={(transaction?: Transaction) => {
            setShowBankTransferModal(false);
            onSuccess(transaction);
            onClose();
          }}
          wallet={wallet}
          amount={parseFloat(amount)}
          fee={calculatedFee}
          preloadedBanks={banks}
        />
      )}

      {/* Mobile Money Modal */}
      {supportsMobileMoney && wallet && dataToUse && (
        <MobileMoneyWithdrawModal
          isOpen={showMobileMoneyModal}
          onClose={() => {
            setShowMobileMoneyModal(false);
            onClose();
          }}
          onSuccess={(transaction?: Transaction) => {
            setShowMobileMoneyModal(false);
            onSuccess(transaction);
            onClose();
          }}
          wallet={wallet}
          amount={parseFloat(amount)}
          fee={calculatedFee}
          preloadedNetworks={networks}
          currencyData={dataToUse}
        />
      )}
    </>
  );
};

export default WithdrawModal;